import { useEffect, useLayoutEffect, useState } from "react";
import type { ReactNode } from "react";
import { Link } from "react-router-dom";
import { ConfigProvider } from "antd";
import { ArrowLeft, Check } from "lucide-react";
import logoMonoWhite from "../../assets/logo/acertei-logo-mono-white.svg";
import { PUBLIC_FONT_FAMILY } from "./publicFontFamily";
import "./public.css";
import "./AuthFrame.css";

/* Auth routes sit outside DefaultLayout's ConfigProvider, so Ant Design
 * would paint its own stack on the inputs and the submit button. The stack
 * is the same one `.public-pages` uses, which is Montserrat. */

export type AuthScreen =
  | "login"
  | "registro"
  | "esqueci-senha"
  | "redefinir-senha"
  | "verificar-email";

const DESKTOP_QUERY = "(min-width: 768px)";

const CHECKS = [
  "Questões separadas por concurso e matéria",
  "Justificativa em cada resposta",
  "Seus acertos por matéria e por tema",
] as const;

const RECOVER_STEPS = [
  "Informe o e-mail da sua conta",
  "Abra o link que enviamos",
  "Crie uma senha nova",
] as const;

const ACTIVATE_STEPS = [
  "Abra o e-mail de ativação",
  "Toque no link para ativar",
  "Entre e comece a treinar",
] as const;

type ScreenCopy = {
  title: string;
  eyebrow: string;
  desktopHeadline: string;
  mobileHeadline: string;
  mobileLine: string;
  items: readonly string[];
  numbered: boolean;
};

const SCREENS: Record<AuthScreen, ScreenCopy> = {
  login: {
    title: "Entrar | Acertei Questões",
    eyebrow: "ÁREA DO ALUNO",
    desktopHeadline: "Treine para o seu concurso resolvendo questões.",
    mobileHeadline: "Que bom te ver de novo.",
    mobileLine: "Seu treino por questões continua aqui.",
    items: CHECKS,
    numbered: false,
  },
  registro: {
    title: "Criar conta | Acertei Questões",
    eyebrow: "CRIE SUA CONTA",
    desktopHeadline: "Seu treino por questões começa aqui.",
    mobileHeadline: "Seu treino começa aqui.",
    mobileLine: "Questões por concurso, com justificativa.",
    items: CHECKS,
    numbered: false,
  },
  "esqueci-senha": {
    title: "Esqueci a senha | Acertei Questões",
    eyebrow: "RECUPERAR ACESSO",
    desktopHeadline: "Vamos recuperar o seu acesso.",
    mobileHeadline: "Vamos recuperar seu acesso.",
    mobileLine: "Em poucos passos você cria uma senha nova.",
    items: RECOVER_STEPS,
    numbered: true,
  },
  "redefinir-senha": {
    title: "Criar nova senha | Acertei Questões",
    eyebrow: "NOVA SENHA",
    desktopHeadline: "Crie uma senha nova e volte a treinar.",
    mobileHeadline: "Crie uma senha nova.",
    mobileLine: "Use uma senha que você não usa em outros sites.",
    items: RECOVER_STEPS,
    numbered: true,
  },
  "verificar-email": {
    title: "Ative sua conta | Acertei Questões",
    eyebrow: "QUASE LÁ",
    desktopHeadline: "Falta só ativar a sua conta.",
    mobileHeadline: "Falta só ativar sua conta.",
    mobileLine: "Depois é só entrar e treinar.",
    items: ACTIVATE_STEPS,
    numbered: true,
  },
};

function useDesktopFrame(): boolean {
  const [desktop, setDesktop] = useState(
    () => window.matchMedia(DESKTOP_QUERY).matches,
  );

  useEffect(() => {
    const media = window.matchMedia(DESKTOP_QUERY);
    const onChange = (event: MediaQueryListEvent) => {
      setDesktop(event.matches);
    };
    setDesktop(media.matches);
    media.addEventListener("change", onChange);
    return () => media.removeEventListener("change", onChange);
  }, []);

  return desktop;
}

function Logo({ variant }: { variant: "band" | "panel" }): JSX.Element {
  return (
    <Link to="/" className={`auth-frame__logo auth-frame__logo--${variant}`}>
      <img src={logoMonoWhite} alt="Acertei Questões" />
    </Link>
  );
}

function DesktopHeadline({ text }: { text: string }): JSX.Element {
  const splitAt = text.lastIndexOf(" ");
  const lead = splitAt === -1 ? "" : text.slice(0, splitAt);
  const last = splitAt === -1 ? text : text.slice(splitAt + 1);

  return (
    <h2 className="auth-frame__headline auth-frame__headline--desktop">
      {lead ? <>{lead} </> : null}
      <span className="auth-frame__last">{last}</span>
    </h2>
  );
}

function BrandList({
  numbered,
  items,
}: {
  numbered: boolean;
  items: readonly string[];
}): JSX.Element {
  const Tag = numbered ? "ol" : "ul";

  return (
    <Tag className="auth-frame__list">
      {items.map((item, index) => (
        <li key={item} className="auth-frame__item">
          <span className="auth-frame__marker" aria-hidden="true">
            {numbered ? index + 1 : <Check size={16} aria-hidden="true" />}
          </span>
          <span className="auth-frame__item-text">{item}</span>
        </li>
      ))}
    </Tag>
  );
}

function QuestionStage(): JSX.Element {
  return (
    <div className="auth-frame__stage" aria-hidden="true">
      <div className="auth-frame__stage-shape auth-frame__stage-shape--teal" />
      <div className="auth-frame__stage-shape auth-frame__stage-shape--emerald" />
      <div className="auth-frame__stage-shape auth-frame__stage-shape--amber" />
      <div className="auth-frame__stage-shape auth-frame__stage-shape--sky" />
      <div className="auth-frame__card">
        <div className="auth-frame__bar auth-frame__bar--lead" />
        <div className="auth-frame__bar auth-frame__bar--long" />
        <div className="auth-frame__bar auth-frame__bar--mid" />
        <div className="auth-frame__choice">
          <span className="auth-frame__letter">A</span>
          <span className="auth-frame__choice-bar auth-frame__choice-bar--wide" />
        </div>
        <div className="auth-frame__choice auth-frame__choice--correct">
          <span className="auth-frame__letter auth-frame__letter--correct">B</span>
          <span className="auth-frame__choice-bar auth-frame__choice-bar--correct" />
          <span className="auth-frame__chip">
            <Check size={13} aria-hidden="true" />
            Correta
          </span>
        </div>
        <div className="auth-frame__choice">
          <span className="auth-frame__letter">C</span>
          <span className="auth-frame__choice-bar auth-frame__choice-bar--short" />
        </div>
      </div>
    </div>
  );
}

export default function AuthFrame({
  screen,
  children,
}: {
  screen: AuthScreen;
  children: ReactNode;
}): JSX.Element {
  const desktop = useDesktopFrame();
  const copy = SCREENS[screen];

  useLayoutEffect(() => {
    document.title = copy.title;
  }, [copy.title]);

  return (
    <div className="public-pages auth-frame">
      <div className="auth-frame__brand">
        <div className="auth-frame__deco" aria-hidden="true">
          <div className="auth-frame__shape auth-frame__shape--emerald" />
          <div className="auth-frame__shape auth-frame__shape--amber" />
          <div className="auth-frame__shape auth-frame__shape--teal" />
          <div className="auth-frame__shape auth-frame__shape--sky" />
        </div>
        <Logo variant="band" />
        <Logo variant="panel" />
        <p className="auth-frame__eyebrow">{copy.eyebrow}</p>
        <DesktopHeadline text={copy.desktopHeadline} />
        <h2 className="auth-frame__headline auth-frame__headline--mobile">
          {copy.mobileHeadline}
        </h2>
        <p className="auth-frame__support">{copy.mobileLine}</p>
        <BrandList numbered={copy.numbered} items={copy.items} />
        <QuestionStage />
      </div>
      <div className="auth-frame__main">
        {desktop ? (
          <Link to="/" className="auth-frame__back">
            <ArrowLeft size={18} aria-hidden="true" />
            <span className="auth-frame__back-label">Voltar para o início</span>
          </Link>
        ) : null}
        <div className="auth-frame__sheet">
          <ConfigProvider theme={{ token: { fontFamily: PUBLIC_FONT_FAMILY } }}>
            {children}
          </ConfigProvider>
        </div>
      </div>
    </div>
  );
}
